import { useEffect } from 'react';
import type { FabricCanvas, FabricObject } from '../types/fabric';
import { resolveCssVar } from '../lib/themeColor';

// Margins are expressed in *screen* pixels and divided by the canvas's
// current zoom before comparing against logical/object-space coordinates —
// not a flat logical-space margin like the original admin plugin used. At
// the zoom levels `fitToViewport()` actually produces here (commonly well
// under 1, since it shrinks the canvas to fit the available workspace), a
// flat 4-logical-px margin shrinks to a sub-2-screen-px hit zone — trivial
// to land on exactly with a synthetic test that sets coordinates directly,
// but next to impossible to hit by hand with a real mouse, especially for
// larger images that tend to need more zoom-out than a small text box.
const CENTER_SCREEN_MARGIN   = 10; // screen px tolerance for snapping to canvas center
const OBJECT_SCREEN_MARGIN   = 10; // screen px tolerance for snapping to another object
const BOUNDARY_SCREEN_MARGIN = 10; // screen px tolerance for snapping to the placeholder's own edge
const OBJECT_LINE_OFFSET = 12; // logical px the object-to-object line extends past the aligned objects
const LINE_WIDTH = 1.5;
const CENTER_LINE_LENGTH_RATIO = 0.75; // canvas-center cross-hair spans 75% of the canvas's height/width, centered

const CENTER_LINE_COLOR   = '#db2777'; // pink-600   — canvas-center cross-hair
const OBJECT_LINE_COLOR   = '#4d7c0f'; // lime-700   — object-to-object snap
// Resolved from the shared --selection token (not a module-load-time
// literal) so this guide line stays in sync with every other
// selection/highlight color in the app from the one place that token lives.
const BOUNDARY_LINE_COLOR = resolveCssVar('--selection', '#16bcdc'); // placeholder/design-area outer-boundary snap

interface VLine { x: number; y1: number; y2: number; }
interface HLine { y: number; x1: number; x2: number; }

function isInRange(a: number, b: number, margin: number): boolean {
  return Math.abs(a - b) <= margin;
}

// Drag-to-align guides for the customer-facing canvas. Three independent
// behaviors, each its own color so they're never ambiguous about *what*
// something snapped to:
//   1. Pink cross-hair    — object's center crosses the canvas center. Drawn
//      at 75% of the canvas's height/width (not full-span), centered — it
//      represents the placeholder's *center point*, not one of its edges.
//   2. Lime line          — object's edge/center aligns with another object's.
//   3. Blue line          — object's edge aligns with the placeholder/design-
//      area's own outer boundary (the canvas's 0/width/height edges).
// This is a TypeScript port of the canonical fabric.js "centering & aligning
// guidelines" plugin already used unmodified for the *admin's* design-area
// editor (`Productdesigner/view/adminhtml/web/js/designareapopup.js`'s
// `initCenteringGuidelines`/`initAligningGuidelines`) — ported here so
// customers placing text/images/clipart get the same behavior admins already
// have when placing design areas, plus the placeholder-boundary snap that
// original didn't need (admin drags *define* the design area, so there's no
// outer boundary to snap to). Unlike that original (admin-only, no pan), this
// version accounts for `canvas.viewportTransform`'s translation component
// too, not just its scale — needed because the customer canvas supports
// panning (`useCanvasPan`) and the original's center-line drawing would
// otherwise land in the wrong place once panned.
//
// Precedence: center > object > boundary, tracked per-axis (X/Y separately)
// via `claimedX`/`claimedY` below. Each handler is registered (and runs) in
// that order on every 'object:moving' event, and only acts on an axis if a
// higher-precedence handler hasn't already claimed it this same event. This
// is a real fix, not just tidiness: without it, a sufficiently narrow design
// area could have a center-snap (purple) on one axis silently overridden by
// a boundary-snap (used to be lime, now blue) on the *same* axis a moment
// later in the handler chain — the object would visibly jump away from
// center the instant it crossed into the boundary's margin too, with no
// indication of why.
export function useAlignmentGuides(canvas: FabricCanvas | null): void {
  useEffect(() => {
    if (!canvas) return;

    const ctx: CanvasRenderingContext2D = canvas.getSelectionContext();

    let isInVerticalCenter   = false;
    let isInHorizontalCenter = false;
    let objectVerticalLines:   VLine[] = [];
    let objectHorizontalLines: HLine[] = [];
    let boundaryVerticalLines:   VLine[] = [];
    let boundaryHorizontalLines: HLine[] = [];

    // Which axis centering already claimed this drag frame — set at the top
    // of `onCenterMove` (registered first) so the handlers after it can see it.
    let claimedX = false;
    let claimedY = false;

    const toScreen = (x: number, y: number): [number, number] => {
      const vt = canvas.viewportTransform as number[];
      return [x * vt[0] + vt[4], y * vt[3] + vt[5]];
    };

    const strokeLine = (x1: number, y1: number, x2: number, y2: number, color: string) => {
      const [sx1, sy1] = toScreen(x1, y1);
      const [sx2, sy2] = toScreen(x2, y2);
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = LINE_WIDTH;
      ctx.beginPath();
      ctx.moveTo(sx1 + 0.5, sy1 + 0.5);
      ctx.lineTo(sx2 + 0.5, sy2 + 0.5);
      ctx.stroke();
      ctx.restore();
    };

    // Returns the logical (unzoomed) canvas dimensions — these are the same
    // coordinate space that getCenterPoint(), getBoundingRect() etc. use.
    // canvas.getWidth()/getHeight() return the *zoomed* pixel dimensions of
    // the DOM element (set by fitToViewport's setDimensions call), which is
    // wrong for snap-position comparisons against object coordinates.
    const getLogical = () => ({
      w: (canvas as any)._originalWidth  ?? (canvas.getWidth()  / (canvas.getZoom() || 1)),
      h: (canvas as any)._originalHeight ?? (canvas.getHeight() / (canvas.getZoom() || 1)),
    });

    // --- 1. Centering guideline (pink) — canvas center only ---
    const onCenterMove = (e: { target?: FabricObject }) => {
      claimedX = false;
      claimedY = false;
      isInVerticalCenter   = false;
      isInHorizontalCenter = false;
      const object = e.target;
      if (!object || !canvas._currentTransform) return;

      // Use logical dimensions (not zoomed pixel dimensions) because
      // getCenterPoint() returns logical object coordinates — comparing them
      // against canvas.getWidth()/getHeight() (zoomed pixels) mixes scales
      // and makes center-snap fail whenever zoom != 1.
      const { w: logW, h: logH } = getLogical();
      const canvasWidthCenter  = logW / 2;
      const canvasHeightCenter = logH / 2;
      const margin = CENTER_SCREEN_MARGIN / (canvas.getZoom() || 1);
      const center = object.getCenterPoint();
      isInVerticalCenter   = isInRange(center.x, canvasWidthCenter, margin);
      isInHorizontalCenter = isInRange(center.y, canvasHeightCenter, margin);
      claimedX = isInVerticalCenter;
      claimedY = isInHorizontalCenter;
      if (isInVerticalCenter || isInHorizontalCenter) {
        object.setPositionByOrigin(
          { x: isInVerticalCenter ? canvasWidthCenter : center.x, y: isInHorizontalCenter ? canvasHeightCenter : center.y },
          'center', 'center',
        );
      }
    };

    // --- 2. Object-to-object guideline (lime) — bounded between the two items ---
    const onObjectAlignMove = (e: { target?: FabricObject }) => {
      objectVerticalLines = [];
      objectHorizontalLines = [];
      const activeObject = e.target;
      if (!activeObject || !canvas._currentTransform) return;

      const margin = OBJECT_SCREEN_MARGIN / (canvas.getZoom() || 1);
      const activeCenter = activeObject.getCenterPoint();
      let activeLeft = activeCenter.x;
      let activeTop  = activeCenter.y;
      const activeRect = activeObject.getBoundingRect();
      const vt = canvas.viewportTransform as number[];
      const activeWidth  = activeRect.width  / vt[0];
      const activeHeight = activeRect.height / vt[3];
      let verticalHit = false;
      let horizontalHit = false;

      for (const other of canvas.getObjects() as FabricObject[]) {
        if (other === activeObject) continue;
        const otherCenter = other.getCenterPoint();
        const otherLeft = otherCenter.x;
        const otherTop  = otherCenter.y;
        const otherRect = other.getBoundingRect();
        const otherWidth  = otherRect.width  / vt[0];
        const otherHeight = otherRect.height / vt[3];

        // Vertical line's y-span covers both objects' merged vertical extent
        // (plus a small offset past whichever is further); horizontal line's
        // x-span covers both objects' merged horizontal extent the same way —
        // so the line always visibly touches both aligned objects, never just
        // floats between them or stretches past them to the canvas edge.
        const vSpan = (activeCenterY: number) => ({
          y1: Math.min(otherTop - otherHeight / 2, activeCenterY - activeHeight / 2) - OBJECT_LINE_OFFSET,
          y2: Math.max(otherTop + otherHeight / 2, activeCenterY + activeHeight / 2) + OBJECT_LINE_OFFSET,
        });
        const hSpan = (activeCenterX: number) => ({
          x1: Math.min(otherLeft - otherWidth / 2, activeCenterX - activeWidth / 2) - OBJECT_LINE_OFFSET,
          x2: Math.max(otherLeft + otherWidth / 2, activeCenterX + activeWidth / 2) + OBJECT_LINE_OFFSET,
        });

        if (!claimedX) {
          // vertical center
          if (isInRange(otherLeft, activeLeft, margin)) {
            verticalHit = true;
            objectVerticalLines.push({ x: otherLeft, ...vSpan(activeTop) });
            activeLeft = otherLeft;
          }
          // left edge
          if (isInRange(otherLeft - otherWidth / 2, activeLeft - activeWidth / 2, margin)) {
            verticalHit = true;
            objectVerticalLines.push({ x: otherLeft - otherWidth / 2, ...vSpan(activeTop) });
            activeLeft = otherLeft - otherWidth / 2 + activeWidth / 2;
          }
          // right edge
          if (isInRange(otherLeft + otherWidth / 2, activeLeft + activeWidth / 2, margin)) {
            verticalHit = true;
            objectVerticalLines.push({ x: otherLeft + otherWidth / 2, ...vSpan(activeTop) });
            activeLeft = otherLeft + otherWidth / 2 - activeWidth / 2;
          }
        }

        if (!claimedY) {
          // horizontal center
          if (isInRange(otherTop, activeTop, margin)) {
            horizontalHit = true;
            objectHorizontalLines.push({ y: otherTop, ...hSpan(activeLeft) });
            activeTop = otherTop;
          }
          // top edge
          if (isInRange(otherTop - otherHeight / 2, activeTop - activeHeight / 2, margin)) {
            horizontalHit = true;
            objectHorizontalLines.push({ y: otherTop - otherHeight / 2, ...hSpan(activeLeft) });
            activeTop = otherTop - otherHeight / 2 + activeHeight / 2;
          }
          // bottom edge
          if (isInRange(otherTop + otherHeight / 2, activeTop + activeHeight / 2, margin)) {
            horizontalHit = true;
            objectHorizontalLines.push({ y: otherTop + otherHeight / 2, ...hSpan(activeLeft) });
            activeTop = otherTop + otherHeight / 2 - activeHeight / 2;
          }
        }
      }

      if (!verticalHit)   objectVerticalLines = [];
      if (!horizontalHit) objectHorizontalLines = [];
      if (verticalHit || horizontalHit) {
        activeObject.setPositionByOrigin({ x: activeLeft, y: activeTop } as any, 'center', 'center');
        claimedX = claimedX || verticalHit;
        claimedY = claimedY || horizontalHit;
      }
    };

    // --- 3. Placeholder/design-area boundary guideline (blue) — canvas's own edges ---
    const onBoundaryMove = (e: { target?: FabricObject }) => {
      boundaryVerticalLines = [];
      boundaryHorizontalLines = [];
      const activeObject = e.target;
      if (!activeObject || !canvas._currentTransform) return;

      const margin = BOUNDARY_SCREEN_MARGIN / (canvas.getZoom() || 1);
      // Logical dimensions — must match the coordinate space of getCenterPoint()
      // and getBoundingRect(). canvas.getWidth()/getHeight() are zoomed pixel
      // dimensions (wrong scale for snap comparisons against object coordinates).
      const { w: canvasW, h: canvasH } = getLogical();
      const activeCenter = activeObject.getCenterPoint();
      let activeLeft = activeCenter.x;
      let activeTop  = activeCenter.y;
      const activeRect = activeObject.getBoundingRect();
      const vt = canvas.viewportTransform as number[];
      const activeWidth  = activeRect.width  / vt[0];
      const activeHeight = activeRect.height / vt[3];
      let verticalHit = false;
      let horizontalHit = false;

      // Drawn the full length of the edge (unlike the object-to-object lines
      // above) since a boundary has no specific "other thing" to bound it to.
      if (!claimedX) {
        if (isInRange(0, activeLeft - activeWidth / 2, margin)) {
          verticalHit = true;
          boundaryVerticalLines.push({ x: 0, y1: 0, y2: canvasH });
          activeLeft = activeWidth / 2;
        } else if (isInRange(canvasW, activeLeft + activeWidth / 2, margin)) {
          verticalHit = true;
          boundaryVerticalLines.push({ x: canvasW, y1: 0, y2: canvasH });
          activeLeft = canvasW - activeWidth / 2;
        }
      }
      if (!claimedY) {
        if (isInRange(0, activeTop - activeHeight / 2, margin)) {
          horizontalHit = true;
          boundaryHorizontalLines.push({ y: 0, x1: 0, x2: canvasW });
          activeTop = activeHeight / 2;
        } else if (isInRange(canvasH, activeTop + activeHeight / 2, margin)) {
          horizontalHit = true;
          boundaryHorizontalLines.push({ y: canvasH, x1: 0, x2: canvasW });
          activeTop = canvasH - activeHeight / 2;
        }
      }

      if (verticalHit || horizontalHit) {
        activeObject.setPositionByOrigin({ x: activeLeft, y: activeTop } as any, 'center', 'center');
      }
    };

    const onMouseDown = () => {
      isInVerticalCenter = isInHorizontalCenter = false;
      objectVerticalLines = []; objectHorizontalLines = [];
      boundaryVerticalLines = []; boundaryHorizontalLines = [];
    };

    const onMouseUp = () => {
      isInVerticalCenter = isInHorizontalCenter = false;
      objectVerticalLines = []; objectHorizontalLines = [];
      boundaryVerticalLines = []; boundaryHorizontalLines = [];
      canvas.renderAll();
    };

    const onBeforeRender = () => {
      canvas.clearContext(canvas.contextTop);
    };

    const onAfterRender = () => {
      // All coordinates passed to strokeLine must be in *logical* space —
      // strokeLine calls toScreen() which multiplies by the viewport zoom.
      // Using canvas.getWidth()/getHeight() (zoomed pixel dimensions) here
      // would double-apply the zoom and draw lines in the wrong position.
      const { w: logW, h: logH } = getLogical();
      const logCenterX = logW / 2;
      const logCenterY = logH / 2;
      // 75% of the logical canvas dimensions, centered — same proportion as
      // before, just in the correct (logical) coordinate space.
      const vInset = logH * (1 - CENTER_LINE_LENGTH_RATIO) / 2;
      const hInset = logW * (1 - CENTER_LINE_LENGTH_RATIO) / 2;
      if (isInVerticalCenter)   strokeLine(logCenterX, vInset, logCenterX, logH - vInset, CENTER_LINE_COLOR);
      if (isInHorizontalCenter) strokeLine(hInset, logCenterY, logW - hInset, logCenterY, CENTER_LINE_COLOR);
      for (const line of objectVerticalLines)   strokeLine(line.x, line.y1, line.x, line.y2, OBJECT_LINE_COLOR);
      for (const line of objectHorizontalLines) strokeLine(line.x1, line.y, line.x2, line.y, OBJECT_LINE_COLOR);
      for (const line of boundaryVerticalLines)   strokeLine(line.x, line.y1, line.x, line.y2, BOUNDARY_LINE_COLOR);
      for (const line of boundaryHorizontalLines) strokeLine(line.x1, line.y, line.x2, line.y, BOUNDARY_LINE_COLOR);
    };

    // Registration order is the precedence order: center, then object, then
    // boundary — each later handler respects `claimedX`/`claimedY` set by
    // whichever ran before it this same 'object:moving' event.
    canvas.on('mouse:down',     onMouseDown);
    canvas.on('object:moving',  onCenterMove);
    canvas.on('object:moving',  onObjectAlignMove);
    canvas.on('object:moving',  onBoundaryMove);
    canvas.on('mouse:wheel',    onMouseUp);
    canvas.on('before:render',  onBeforeRender);
    canvas.on('after:render',   onAfterRender);
    canvas.on('mouse:up',       onMouseUp);

    return () => {
      canvas.off('mouse:down',     onMouseDown);
      canvas.off('object:moving',  onCenterMove);
      canvas.off('object:moving',  onObjectAlignMove);
      canvas.off('object:moving',  onBoundaryMove);
      canvas.off('mouse:wheel',    onMouseUp);
      canvas.off('before:render',  onBeforeRender);
      canvas.off('after:render',   onAfterRender);
      canvas.off('mouse:up',       onMouseUp);
      // `canvas` here is the side that was active when this effect ran, not
      // necessarily the side that's active now. By the time a side switch
      // gets here, `useFabricCanvas`'s own cleanup may have already disposed
      // this exact canvas instance (it depends on the same side-change), which
      // nulls out `contextTop` — so this must tolerate that, not assume it.
      if (canvas.contextTop) canvas.clearContext(canvas.contextTop);
    };
  }, [canvas]);
}
